import React, { FC } from "react";

import { ProspectorBusinessQuery } from "generated/graphql";
import { formatLabel } from "utils/formatLabel";

import classes from "../containers/V3/style.scss";

type ProspectorBusinessResult = NonNullable<ProspectorBusinessQuery["public"]["business"]>;

export type CompetitiveInsightsPeers = ProspectorBusinessResult["peers"];
export type CompetitiveInsightsCompetitors = ProspectorBusinessResult["competitors"];

export interface CompetitiveInsightsProps {
    peers?: CompetitiveInsightsPeers;
    competitors?: CompetitiveInsightsCompetitors;
    vertical?: string | null;
    /**
     * The subject business's identity, used to filter it out of its own competitor
     * list — prospector can return a duplicate crawl record of the subject (same
     * name, different businessId), which we must not present as a competitor.
     */
    subjectBusinessId?: string | null;
    subjectName?: string | null;
}

function normalizeName(name?: string | null): string {
    return (name ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

/**
 * The book-online rate from the most-local peer bucket that has members, falling
 * back city -> state -> national. Empty buckets (count 0) are skipped so an empty
 * local scope never hides a usable broader-scope rate. Returns null when no scope
 * has members.
 */
function getMostLocalBookOnlineRate(peers?: CompetitiveInsightsPeers): number | null {
    const buckets = [peers?.city, peers?.state, peers?.national];
    for (const bucket of buckets) {
        if (bucket && bucket.count > 0) {
            return bucket.bookOnlineRate;
        }
    }
    return null;
}

export const CompetitiveInsights: FC<CompetitiveInsightsProps> = ({
    peers,
    competitors,
    vertical,
    subjectBusinessId,
    subjectName,
}) => {
    const bookOnlineRate = getMostLocalBookOnlineRate(peers);

    const subjectNormalizedName = normalizeName(subjectName);
    const bookOnlineCompetitor = (competitors ?? [])
        .filter(
            (c) =>
                c.hasBookOnline &&
                // Exclude the subject business itself (by id, and by normalized name to
                // catch duplicate crawl records that share the name but not the id).
                c.businessId !== subjectBusinessId &&
                (!subjectNormalizedName || normalizeName(c.name) !== subjectNormalizedName)
        )
        // The copy says "near you", so pick the nearest — prospector orders
        // competitors by a rating-weighted score, not strictly by distance.
        .sort((a, b) => a.distanceMiles - b.distanceMiles)[0];

    // Each condition below mirrors a rendered <li>; if neither can render, render
    // nothing rather than an empty list.
    if (bookOnlineRate == null && !bookOnlineCompetitor) {
        return null;
    }

    // Verticals arrive as raw slugs (e.g. "hvac_contractor"); format for display.
    const verticalLabel = vertical ? formatLabel(vertical) : "local";

    return (
        <ul className={classes.asideList} style={{ marginTop: "1em" }}>
            {bookOnlineRate != null && (
                <li>
                    <b>{Math.round(bookOnlineRate * 100)}%</b> of {verticalLabel} businesses offer online booking
                </li>
            )}
            {bookOnlineCompetitor && (
                <li>
                    A competitor near you (<b>{bookOnlineCompetitor.name}</b>) lets customers book online
                </li>
            )}
        </ul>
    );
};
